<script setup lang="ts">
import { computed, ref } from 'vue';

import {
  type PlaybookEvaluation,
  type PlaybookEvaluationOption,
  type PlaybookEvaluationResult,
  evaluationTypeLabel,
  extractedValue,
  extractionResponseType,
} from '@/services/playbooks/playbookRunDisplay';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaExtractionCard from '@/components/design/review/GaiaExtractionCard.vue';
import GaiaFieldRow from '@/components/design/base/GaiaFieldRow.vue';
import GaiaTabs from '@/components/design/base/GaiaTabs.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import ClassificationResultBody from './ClassificationResultBody.vue';
import ClauseValidationResultBody from './ClauseValidationResultBody.vue';
import ExtractionResultBody from './ExtractionResultBody.vue';
import DownstreamActionsList from './DownstreamActionsList.vue';
import ResultEvidence from './ResultEvidence.vue';

const props = defineProps<{
  evaluation: PlaybookEvaluation;
  result: PlaybookEvaluationResult;
  optionMap: Map<string, PlaybookEvaluationOption>;
}>();

const emit = defineEmits<{
  highlight: [payload: { page: number; texts: string[] }];
}>();

const matchedOptions = computed<PlaybookEvaluationOption[]>(() =>
  (props.result.matched_option_uids ?? [])
    .map(uid => props.optionMap.get(uid))
    .filter((option): option is PlaybookEvaluationOption => option !== undefined)
);

const isClassification = computed<boolean>(
  () => props.evaluation.evaluation_type === 'classification'
);

const isExtraction = computed<boolean>(() => props.evaluation.evaluation_type === 'extraction');

const configuredActions = computed(() =>
  matchedOptions.value.flatMap(option => option.actions ?? [])
);

// Only the matched options' own actions are listed here; group-triggered actions
// are recorded on an arbitrary member evaluation and have their own section.
const hasActions = computed<boolean>(() => configuredActions.value.length > 0);

const page = computed<number>(() => props.result.page_number ?? 1);

/** The card is headed by what kind of answer this is, as the review detail's is. */
const cardTitle = computed<string>(() => evaluationTypeLabel(props.evaluation.evaluation_type));

/** The passages the answer came from — the "Trace" the card's action opens. */
const snippets = computed<string[]>(() =>
  (props.result.highlighted_text ?? []).map(text => text.trim()).filter(text => text.length > 0)
);

const traceOpen = ref<boolean>(false);

function highlightSnippet(text: string): void {
  emit('highlight', { page: page.value, texts: [text] });
}
</script>

<template>
  <gaia-tabs type="line" size="small" animated>
    <n-tab-pane name="result" tab="Result">
      <n-flex vertical :size="12">
        <gaia-banner v-if="result.error">{{ result.error }}</gaia-banner>

        <gaia-extraction-card :title="cardTitle" subtitle="Information from document">
          <template v-if="snippets.length" #actions>
            <gaia-button size="tiny" @click="traceOpen = !traceOpen">
              {{ traceOpen ? 'Hide trace' : 'Trace' }}
            </gaia-button>
          </template>

          <gaia-field-row label="Response" :label-width="86" :divider="false">
            <ExtractionResultBody
              v-if="isExtraction"
              :value="extractedValue(result)"
              :response-type="extractionResponseType(evaluation)"
            />
            <ClassificationResultBody
              v-else-if="isClassification"
              :matched-options="matchedOptions"
            />
            <ClauseValidationResultBody v-else :matched-options="matchedOptions" />
          </gaia-field-row>

          <gaia-field-row v-if="result.reasoning" label="Reason" :label-width="86" :divider="false">
            <gaia-text :style="{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }">
              {{ result.reasoning }}
            </gaia-text>
          </gaia-field-row>

          <gaia-field-row v-if="result.page_number" label="Page" :label-width="86" :divider="false">
            {{ result.page_number }}
          </gaia-field-row>

          <ResultEvidence
            v-if="traceOpen"
            :snippets="snippets"
            @highlight-snippet="highlightSnippet"
          />
        </gaia-extraction-card>
      </n-flex>
    </n-tab-pane>
    <n-tab-pane v-if="hasActions" name="actions" tab="Actions">
      <DownstreamActionsList
        :actions="configuredActions"
        :action-results="result.action_results ?? []"
      />
    </n-tab-pane>
  </gaia-tabs>
</template>
